import type { ChartModuleId } from '../domain/types';

export type KnowledgeKind = '基础原典' | '术数专书' | '医史文献' | '现代开源工具';

export interface KnowledgeEntry {
  id: string;
  title: string;
  era: string;
  attribution: string;
  kind: KnowledgeKind;
  systems: ChartModuleId[];
  summary: string;
  keyConcepts: string[];
  provenanceNote?: string;
  sourceUrl?: string;
}

export const knowledgeEntries: KnowledgeEntry[] = [
  {
    id: 'zhouyi', title: '《周易》', era: '先秦', attribution: '作者与成书层累复杂', kind: '基础原典', systems: ['liuyao', 'meihua', 'huangji-jingshi'],
    summary: '六十四卦卦爻辞的基础文本。本站把经文、算法规则和后世解释分层展示，不把后世术数规则伪装成经文原义。',
    keyConcepts: ['八卦', '六十四卦', '卦辞', '爻辞', '变爻'], sourceUrl: 'https://ctext.org/book-of-changes/zh'
  },
  {
    id: 'yizhuan', title: '《易传》', era: '战国至汉初', attribution: '传统题孔子，现代研究多视为多篇累积', kind: '基础原典', systems: ['liuyao', 'meihua', 'huangji-jingshi'],
    summary: '包含彖、象、系辞、说卦等篇，为象数、义理和八卦取象提供重要文本背景。',
    keyConcepts: ['十翼', '彖传', '象传', '系辞', '说卦'], sourceUrl: 'https://ctext.org/book-of-changes/zh'
  },
  {
    id: 'jingfang', title: '《京氏易传》', era: '西汉', attribution: '京房', kind: '术数专书', systems: ['liuyao'],
    summary: '京房易学的重要材料，后世纳甲、八宫卦序和世应体系的重要来源之一。',
    keyConcepts: ['八宫', '纳甲', '世应', '卦气'], sourceUrl: 'https://ctext.org/wiki.pl?if=gb&res=771896'
  },
  {
    id: 'huozhulin', title: '《火珠林》', era: '五代至宋间传本', attribution: '作者及成书年代有争议', kind: '术数专书', systems: ['liuyao'],
    summary: '六爻火珠林法的重要传本，涉及纳甲、六亲、月建日辰等判断框架。',
    keyConcepts: ['六亲', '月建', '日辰', '用神', '纳甲'], provenanceNote: '不同传本题署和内容存在差异。'
  },
  {
    id: 'zengshanbuyi', title: '《增删卜易》', era: '清', attribution: '野鹤老人原著，李文辉增删', kind: '术数专书', systems: ['liuyao'],
    summary: '以大量卦例讨论用神、动变、旬空、月破、进退和生克制化，是六爻实践传统中的常用文献。',
    keyConcepts: ['用神', '动变', '旬空', '月破', '进神退神']
  },
  {
    id: 'bushizhengzong', title: '《卜筮正宗》', era: '清', attribution: '王洪绪', kind: '术数专书', systems: ['liuyao'],
    summary: '系统整理六爻歌诀、用神、六亲和断卦规则。本站引用规则时标明本书层级，不与《周易》经文混同。',
    keyConcepts: ['黄金策', '用神', '六亲', '断易']
  },
  {
    id: 'meihua', title: '《梅花易数》', era: '宋以后传本', attribution: '传统题邵雍，成书与传承仍有争议', kind: '术数专书', systems: ['meihua'],
    summary: '通行时间起卦、数字起卦以及体用、生克、互卦、变卦框架的主要文本来源。',
    keyConcepts: ['体用', '互卦', '变卦', '时间起卦', '数字起卦'], provenanceNote: '本站将“传统题署”与可核实作者身份分开标注。'
  },
  {
    id: 'yanbodiao', title: '《烟波钓叟歌》', era: '明以前流传', attribution: '题署与作者有争议', kind: '术数专书', systems: ['qimen'],
    summary: '奇门遁甲常用歌诀文献，涉及阴阳遁、局数、九星八门和格局。',
    keyConcepts: ['阴阳遁', '九星', '八门', '值符', '值使']
  },
  {
    id: 'dunjiayanyi', title: '《遁甲演义》', era: '明清传本', attribution: '题署与版本多样', kind: '术数专书', systems: ['qimen'],
    summary: '奇门排局与格局解释的重要传统资料之一；不同门派在置闰、拆补、转盘、飞盘等处存在口径差异。',
    keyConcepts: ['拆补', '置闰', '转盘', '飞盘', '九宫'], provenanceNote: '算法页会明确显示当前采用的排盘口径。'
  },
  {
    id: 'liurenzhinan', title: '《大六壬指南》', era: '明', attribution: '陈公献', kind: '术数专书', systems: ['liuren'],
    summary: '大六壬课体与占验传统的重要资料，涵盖四课三传、天将和课体分析。',
    keyConcepts: ['四课', '三传', '天将', '九宗门', '课体']
  },
  {
    id: 'liurendaquan', title: '《六壬大全》', era: '明清传本', attribution: '汇编性质，版本系统复杂', kind: '术数专书', systems: ['liuren', 'jinkoujue'],
    summary: '汇集六壬起课、课体、神煞和判断资料。使用时需区分可计算课式、后世歌诀与具体占验。',
    keyConcepts: ['天地盘', '月将', '贵人', '四课三传', '神煞']
  },
  {
    id: 'jinkou', title: '《六壬神课金口诀古本》', era: '传本年代不一', attribution: '作者与版本源流有争议', kind: '术数专书', systems: ['jinkoujue'],
    summary: '金口诀四位起课、贵神、遁干、阴阳取用及五动三动的传统依据。',
    keyConcepts: ['地分', '将神', '贵神', '人元', '五动三动']
  },
  {
    id: 'taiyijinjiang', title: '《太乙金镜式经》', era: '唐', attribution: '王希明', kind: '术数专书', systems: ['taiyi'],
    summary: '太乙式的重要传世文献，涉及积年、局数、太乙及主客算等结构。',
    keyConcepts: ['太乙', '积年', '局数', '主算', '客算']
  },
  {
    id: 'yuanhaiziping', title: '《渊海子平》', era: '宋元至明传本', attribution: '传统题徐大升编', kind: '术数专书', systems: ['bazi'],
    summary: '子平命理的重要汇编，涉及十神、格局、六亲和岁运。',
    keyConcepts: ['四柱', '十神', '格局', '大运', '六亲']
  },
  {
    id: 'sanmingtonghui', title: '《三命通会》', era: '明', attribution: '万民英', kind: '术数专书', systems: ['bazi'],
    summary: '汇集命理文献与法则的大型著作，资料丰富但内部兼收多说，不能视为单一无分歧算法。',
    keyConcepts: ['五行', '神煞', '格局', '纳音', '岁运']
  },
  {
    id: 'ditiansui', title: '《滴天髓》及任氏注', era: '明清传本', attribution: '原作者题署有争议，清任铁樵注', kind: '术数专书', systems: ['bazi'],
    summary: '以旺衰、气势、通关、调候等讨论命局结构；原文与后世注解应分层阅读。',
    keyConcepts: ['旺衰', '气势', '通关', '清浊', '任氏注'], provenanceNote: '“原文作者”与任铁樵注释不是同一层文本。'
  },
  {
    id: 'qiongtongbaojian', title: '《穷通宝鉴》', era: '清代整理本', attribution: '由《拦江网》等材料整理流传', kind: '术数专书', systems: ['bazi'],
    summary: '按月令讨论调候与天干取用，是八字调候传统的重要材料。',
    keyConcepts: ['月令', '调候', '取用', '寒暖燥湿']
  },
  {
    id: 'ziweiquanshu', title: '《紫微斗数全书》', era: '明清传本', attribution: '传统托名陈抟，实际成书源流复杂', kind: '术数专书', systems: ['ziwei'],
    summary: '紫微斗数安星、十二宫、星曜性质与歌诀的重要传本。',
    keyConcepts: ['十二宫', '安星', '十四主星', '四化', '运限'], provenanceNote: '本站不把传统托名当作已经证实的作者结论。'
  },
  {
    id: 'xiejibianfang', title: '《钦定协纪辨方书》', era: '清', attribution: '梅瑴成等奉敕编纂', kind: '术数专书', systems: ['huangli'],
    summary: '传统择日、神煞与历法辨析的大型汇编。适合研究历史择日体系，不应替代现代安全与专业决策。',
    keyConcepts: ['择日', '建除', '神煞', '宜忌', '历法']
  },
  {
    id: 'suwen-yunqi', title: '《黄帝内经·素问》运气七篇', era: '汉以前至后世整理', attribution: '多层累积文本', kind: '医史文献', systems: ['wuyun-liuqi'],
    summary: '五运六气理论的核心文献背景，包括天干化运、司天在泉与六气分步。本站只作医学史与传统知识整理。',
    keyConcepts: ['五运', '六气', '司天', '在泉', '天元纪大论'], sourceUrl: 'https://ctext.org/huangdi-neijing/zh'
  },
  {
    id: 'huangjijingshi', title: '《皇极经世》', era: '北宋', attribution: '邵雍', kind: '术数专书', systems: ['huangji-jingshi'],
    summary: '以元、会、运、世等周期和象数框架讨论历史时间秩序。',
    keyConcepts: ['元会运世', '先天图', '值年卦', '象数']
  },
  {
    id: 'shenxiangquanbian', title: '《神相全编》', era: '宋以后传本', attribution: '传统题陈抟，版本与成书源流复杂', kind: '术数专书', systems: [],
    summary: '传统相术文献汇编，涉及五官、三停、五岳与气色等历史术语。本站仅作民俗史与文本研究，不据此判断身份、健康、性格或命运。',
    keyConcepts: ['五官', '三停', '五岳', '气色', '相术史'], sourceUrl: 'https://ctext.org/wiki.pl?chapter=905153&if=gb&remap=gb'
  },
  {
    id: 'maiyixiangfa', title: '《麻衣相法》', era: '明清传本', attribution: '传统托名麻衣道人，作者与版本有争议', kind: '术数专书', systems: [],
    summary: '民间相术传统中的常见书名与传本集合，常见十二宫、骨相和部位说法。本站只整理文献语境，不把相书断语当作科学证据或现实预测。',
    keyConcepts: ['十二宫', '骨相', '部位', '相法传本', '民俗史']
  },
  {
    id: 'mingyu-core', title: 'mingyu-core', era: '当代', attribution: 'Brhiza 与开源贡献者', kind: '现代开源工具', systems: ['liuyao', 'meihua', 'qimen', 'liuren', 'xiaoliuren', 'taiyi', 'bazi', 'ziwei', 'jinkoujue', 'huangli', 'wuyun-liuqi', 'huangji-jingshi'],
    summary: '本站使用的主要确定性算法底座，提供排盘结果、证据字段、边界说明和可复现输入。',
    keyConcepts: ['TypeScript', '确定性算法', '证据链', 'MIT'], sourceUrl: 'https://www.npmjs.com/package/mingyu-core'
  },
  {
    id: 'iztro', title: 'iztro', era: '当代', attribution: 'SylarLong 与开源贡献者', kind: '现代开源工具', systems: ['ziwei'],
    summary: '紫微斗数星盘计算开源库，作为紫微模块的底层依赖，由 mingyu-core 统一封装。',
    keyConcepts: ['紫微斗数', '星盘', 'TypeScript', 'MIT'], sourceUrl: 'https://github.com/SylarLong/iztro'
  },
  {
    id: 'mediapipe-face-landmarker', title: 'MediaPipe Face Landmarker', era: '当代', attribution: 'Google AI Edge 与开源贡献者', kind: '现代开源工具', systems: [],
    summary: '浏览器视觉任务工具，可在图片或视频中定位人脸关键点，适合做图像结构标注；关键点检测不等于身份识别，也不能证明传统相术结论。',
    keyConcepts: ['人脸关键点', '浏览器视觉', 'Web', '隐私边界'], sourceUrl: 'https://github.com/google-ai-edge/mediapipe/blob/master/mediapipe/tasks/web/vision/README.md'
  },
  {
    id: 'mediapipe-hand-landmarker', title: 'MediaPipe Hand Landmarker', era: '当代', attribution: 'Google AI Edge 与开源贡献者', kind: '现代开源工具', systems: [],
    summary: '浏览器视觉任务工具，可在图片或视频中定位手部关键点，适合做手部轮廓与结构辅助；关键点检测不等于手相判断或未来预测。',
    keyConcepts: ['手部关键点', '浏览器视觉', 'Web', '辅助标注'], sourceUrl: 'https://github.com/google-ai-edge/mediapipe/blob/master/mediapipe/tasks/web/vision/README.md'
  }
];

function ctextSearchUrl(title: string): string {
  const query = title.replace(/[《》]/g, '');
  return `https://ctext.org/wiki.pl?if=gb&remap=gb&searchu=${encodeURIComponent(query)}`;
}

export function sourceUrlFor(entry: KnowledgeEntry): string {
  if (!entry.sourceUrl) return ctextSearchUrl(entry.title);
  if (entry.sourceUrl.startsWith('https://ctext.org/') && entry.sourceUrl.endsWith('/zh')) {
    return `${entry.sourceUrl}s`;
  }
  return entry.sourceUrl;
}

function normalize(value: string): string {
  return value.toLocaleLowerCase('zh-CN').replace(/[《》·\s]/g, '');
}

export function searchKnowledge(query: string, system?: ChartModuleId): KnowledgeEntry[] {
  const term = normalize(query);
  return knowledgeEntries
    .filter((entry) => !system || entry.systems.includes(system))
    .map((entry) => {
      if (!term) return { entry, score: 1 };
      const title = normalize(entry.title);
      const concepts = normalize(entry.keyConcepts.join(' '));
      const body = normalize(`${entry.summary} ${entry.era} ${entry.attribution}`);
      const score = (title.includes(term) ? 8 : 0) + (concepts.includes(term) ? 4 : 0) + (body.includes(term) ? 2 : 0);
      return { entry, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title, 'zh-CN'))
    .map((item) => item.entry);
}

export function knowledgeContextFor(system: ChartModuleId, limit = 5): string[] {
  return searchKnowledge('', system)
    .filter((entry) => entry.kind !== '现代开源工具')
    .slice(0, limit)
    .map((entry) => `${entry.title}（${entry.era}）：${entry.summary} 关键词：${entry.keyConcepts.join('、')}。`);
}

export type ImageKnowledgeKind = 'palm' | 'face';

export function imageKnowledgeContextFor(kind: ImageKnowledgeKind, limit = 4): string[] {
  const ids = kind === 'palm'
    ? ['maiyixiangfa', 'shenxiangquanbian', 'mediapipe-hand-landmarker']
    : ['maiyixiangfa', 'shenxiangquanbian', 'mediapipe-face-landmarker'];
  return ids
    .map((id) => knowledgeEntries.find((entry) => entry.id === id))
    .filter((entry): entry is KnowledgeEntry => Boolean(entry))
    .slice(0, limit)
    .map((entry) => `${entry.title}（${entry.era}）：${entry.summary} 关键词：${entry.keyConcepts.join('、')}。`);
}
